import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ResumeStepValidatable } from '@edtech-ui/core/models/resume-builder/step-validators';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-resumer-builder-setup',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './resumer-builder-setup.component.html',
	styleUrl: './resumer-builder-setup.component.scss'
})
export class ResumerBuilderSetupComponent implements OnInit, OnDestroy, ResumeStepValidatable {

	includePhoto = false;
	selectedTemplate = 'Classic';
	previewUrl: string | ArrayBuffer | null = null;
	selectedFile: File | null = null;
	subscription = new Subscription();
	showPhoto: boolean = false;
	resumeForm!: FormGroup;

	constructor(
		private resumeService: ResumeService,
		private router: Router,
		private _toastr: ToastrService
	) {
		this.resumeForm = new FormGroup({
			target_role: new FormControl(''),
			experience_level: new FormControl('fresher'),
			template_id: new FormControl(this.selectedTemplate, Validators.required),
			theme_color: new FormControl(this.selectedColor, Validators.required),
		})
	}

	ngOnInit(): void {

		const savedData = this.resumeService.getSetupData();
		const resumeId = this.resumeService.getResumeId();

		if (savedData) {
			this.resumeForm.patchValue(savedData);
			this.selectedTemplate = savedData.template_id;
			this.selectedColor = savedData.theme_color;
			this.previewUrl = savedData.previewUrl;
			this.includePhoto = savedData.includePhoto;
		}

		if (resumeId) {
			this.subscription.add(
				this.resumeService.getStudentResumeById(resumeId).pipe(
					tap(res => {
						if (res.Success) {
							this.editStudentResumeData(res.Data);
							this.resumeService.setSetupData({
								target_role: res.Data.targetRole,
								experience_level: res.Data.experienceLevel,
								template_id: res.Data.config?.templateId,
								theme_color: res.Data.config?.themeColor,
								previewUrl: res.Data.config?.photoUrl,
								includePhoto: res.Data.config?.includePhoto
							});
						}
					}),
					catchError(() => {
						this._toastr.error('Failed to load resume data');
						return of(null);
					})
				).subscribe()
			); return;
		}

		this.subscription.add(
			this.resumeService.getFullResumeData().subscribe(data => {
				if (data) {
					this.editStudentResumeData(data);
				}
			})
		)
	}

	getFormData() {
		return { ...this.resumeForm.value, file: this.includePhoto ? this.selectedFile:null };
	}


	templates = [
		{ name: 'Classic', type: 'one-col' },
		{ name: 'Modern', type: 'one-col' },
		{ name: 'Minimal', type: 'one-col' },
		{ name: 'Professional', type: 'one-col' },
		{ name: 'Sidebar', type: 'two-col' },
		{ name: 'Executive', type: 'two-col' },
		{ name: 'Creative', type: 'two-col' },
		{ name: 'Compact', type: 'two-col' }
	];


	colors = ['#1e293b', '#1e3a5f', '#475569', '#2563eb', '#166534', '#0d9488', '#881337', '#6b21a8', '#78350f', '#374151', '#0f172a'];

	selectedColor = this.colors[0];


	setPhoto(val: boolean) {
		this.includePhoto = val;
		if (!val) {
			this.selectedFile = null;
			this.previewUrl = null;
			this.resumeForm.patchValue({ file: null });
		}
	}

	selectTemplate(t: string) {
		this.selectedTemplate = t;
		this.resumeForm.patchValue({ template_id: t });
	}


	selectColor(c: string) {
		this.selectedColor = c;
		this.resumeForm.patchValue({ theme_color: c });
	}


	onFileSelected(event: any) {
		const file = event.target.files[0];

		if (!file) return;

		if (file.size > 5 * 1024 * 1024) {
			this._toastr.warning('File size should be less than 5MB');
			return;
		}

		this.selectedFile = file;
		this.resumeForm.patchValue({ file: file });
		const reader = new FileReader();
		reader.onload = () => {
			this.previewUrl = reader.result;
		};
		reader.readAsDataURL(file);
	}


	editStudentResumeData(data: any) {
		if (!data) return;

		this.resumeForm.patchValue({
			target_role: data.targetRole || '',
			experience_level: data.experienceLevel || 'fresher',
			template_id: data.config?.templateId || this.selectedTemplate,
			theme_color: data.config?.themeColor || this.selectedColor
		});
		this.selectedTemplate = data.config?.templateId || 'Classic';
		this.selectedColor = data.config?.themeColor || this.colors[0];

		if (data.config?.includePhoto && data.config?.photoUrl) {
			this.includePhoto = true;
			this.previewUrl = data.config.photoUrl;
		} else {
			this.includePhoto = false;
		}
	}


	isFormValid(): boolean {
		return this.resumeForm.valid
			&& !!this.selectedTemplate
			&& !!this.selectedColor;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}